// ══════════════════════════════════════════════════════════════════
//  ED CRM - Filtros por usuário (defesa em profundidade)
// ══════════════════════════════════════════════════════════════════
//  O RLS de Config/27 já garante o isolamento. Estes helpers aplicam
//  o mesmo filtro no client para:
//   * Reduzir o payload (queries menores quando role = sdr/closer).
//   * Tornar a intenção explícita no código.
//   * Facilitar debug ("por que esse SDR vê esse card?" — vira óbvio).
//
//  Tabelas suportadas:
//    'cards'      → sdr_id ou closer_id = user.id  (OR)
//    'meetings'   → sdr_id ou closer_id = user.id  (OR)
//    'activities' → assigned_to = user.id
//    'commissions'→ closer_id ou sdr_id = user.id  (OR)
//    'leads'      → sdr_id ou closer_id = user.id  (OR)
// ══════════════════════════════════════════════════════════════════

import { isEnterpriseWorkspace } from '@config/enterprise';

export type OperationalRole = 'gestor' | 'closer' | 'sdr' | 'aex';

export interface UserFilterContext {
  id: string | null;
  role: OperationalRole | null;
  canViewAll: boolean;
  enterpriseMode?: boolean;
}

// Assinatura mínima de um query builder do Supabase que usamos aqui.
export interface FilterableQuery {
  eq(column: string, value: string): this;
  or?(filters: string): this;
}

interface TableFilterConfig {
  sdr: string;
  closer: string;
  mode: 'or' | 'eq';
}

const TABLE_FILTERS: Record<string, TableFilterConfig> = {
  cards:       { sdr: 'sdr_id',      closer: 'closer_id',    mode: 'or' },
  meetings:    { sdr: 'sdr_id',      closer: 'closer_id',    mode: 'or' },
  leads:       { sdr: 'sdr_id',      closer: 'closer_id',    mode: 'or' },
  commissions: { sdr: 'sdr_id',      closer: 'closer_id',    mode: 'or' },
  activities:  { sdr: 'assigned_to', closer: 'assigned_to',  mode: 'eq' },
};

// currentUser: { id, role, canViewAll }
//   role: 'gestor' | 'closer' | 'sdr' | null
//   canViewAll: boolean — owner ou gestor (operacional/admin)
//
// Genérico sem constraint: o builder tipado do Supabase restringe as colunas
// de `.eq` a nomes conhecidos, então não é estruturalmente um subtipo de
// FilterableQuery (que aceita `string`). Recebemos `Q` livre e fazemos o cast
// interno localizado para chamar `.eq` com nomes dinâmicos de coluna.
export const applyUserFilter = <Q>(
  query: Q,
  table: string,
  currentUser: UserFilterContext | null | undefined,
): Q => {
  if (!query || !currentUser) return query;
  if (currentUser.canViewAll) return query;

  const cfg = TABLE_FILTERS[table];
  if (!cfg) return query;

  const userId = currentUser.id;
  if (!userId) return query;

  const q = query as unknown as FilterableQuery;

  if (currentUser.role === 'aex') {
    if (cfg.mode === 'or' && typeof q.or === 'function') {
      return q.or(`${cfg.sdr}.eq.${userId},${cfg.closer}.eq.${userId}`) as unknown as Q;
    }
    return q.eq(cfg.sdr, userId) as unknown as Q;
  }

  if (currentUser.role === 'sdr') {
    // SDR vê apenas onde a coluna SDR aponta para ele.
    return q.eq(cfg.sdr, userId) as unknown as Q;
  }

  if (currentUser.role === 'closer') {
    if (currentUser.enterpriseMode && cfg.mode === 'or' && typeof q.or === 'function') {
      return q.or(`${cfg.closer}.eq.${userId},${cfg.sdr}.eq.${userId}`) as unknown as Q;
    }
    // Closer vê apenas onde a coluna Closer aponta para ele.
    return q.eq(cfg.closer, userId) as unknown as Q;
  }

  // Sem role operacional definida e sem can_view_all — não retorna nada.
  // Usa um filtro impossível para garantir resultado vazio.
  return q.eq('id', '00000000-0000-0000-0000-000000000000') as unknown as Q;
};

// Estrutura mínima do useAuth usada por makeUserContext.
interface AuthLike {
  user?: { id?: string | null } | null;
  workspace?: unknown;
  userRole?: () => string | null;
  canViewAll?: () => boolean;
}

// Helper para extrair o "currentUser" do useAuth de forma consistente.
//
//   const ctx = makeUserContext(useAuth());
//   applyUserFilter(query, 'cards', ctx);
export const makeUserContext = (auth: AuthLike | null | undefined): UserFilterContext | null => {
  if (!auth) return null;
  const enterpriseMode = isEnterpriseWorkspace(auth.workspace);
  return {
    id:          auth.user?.id || null,
    role:        (typeof auth.userRole === 'function' ? auth.userRole() : null) as OperationalRole | null,
    canViewAll:  typeof auth.canViewAll === 'function' ? auth.canViewAll() : false,
    ...(enterpriseMode ? { enterpriseMode } : {}),
  };
};

// Verifica se um usuário pode editar um campo específico de um card,
// dado o role.  Usado em UI para desabilitar inputs antecipadamente
// (RLS bloqueia no banco; UI dá feedback).
export const canEditCardField = (currentUser: unknown, _fieldKey?: string): boolean => {
  if (!currentUser) return false;
  return true;
};
